import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { CameraIcon, CheckIcon, Loader2Icon, RefreshCcwIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { updateProfileSchema } from '@shared/schemas/auth';
import {
  finalizeAvatarUpload,
  getBrowserUrl,
  prepareAvatarUpload,
  sendVerificationEmail,
  updateCurrentUser,
} from '@/lib/auth-client';
import { getAvatarAcceptedFileTypes, processAvatarUpload } from '@/lib/avatar-upload';
import { getAuthErrorMessage } from '@/lib/auth-errors';
import { cn } from '@/lib/utils';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

type AccountProfileFormProps = React.ComponentProps<'form'> & {
  onAccountUpdated: () => Promise<void>;
  session: NonNullable<ReturnType<typeof import('@/lib/auth-client').authClient.useSession>['data']>;
};

type UpdateProfileFormInput = z.input<typeof updateProfileSchema>;
type UpdateProfileFormOutput = z.output<typeof updateProfileSchema>;
type AvatarUploadState = {
  file: File;
  previewUrl: string;
};

function getInitials(name: string) {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');
}

export function AccountProfileForm({
  className,
  onAccountUpdated,
  session,
  ...props
}: AccountProfileFormProps) {
  const avatarInputRef = useRef<HTMLInputElement | null>(null);
  const [avatarUpload, setAvatarUpload] = useState<AvatarUploadState | null>(null);
  const [isAvatarProcessing, setIsAvatarProcessing] = useState(false);
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const form = useForm<UpdateProfileFormInput, undefined, UpdateProfileFormOutput>({
    defaultValues: {
      image: session.user.image ?? '',
      name: session.user.name,
    },
    resolver: zodResolver(updateProfileSchema),
  });
  const avatarUrl = form.watch('image');
  const avatarInitials = getInitials(form.watch('name') || session.user.name);

  useEffect(() => {
    form.reset({
      image: session.user.image ?? '',
      name: session.user.name,
    });
  }, [form, session.user.image, session.user.name]);

  useEffect(() => {
    return () => {
      if (avatarUpload?.previewUrl) {
        URL.revokeObjectURL(avatarUpload.previewUrl);
      }
    };
  }, [avatarUpload]);

  const displayedAvatarUrl = avatarUpload?.previewUrl ?? avatarUrl;
  const isBusy = form.formState.isSubmitting || isAvatarProcessing || isAvatarUploading;

  function updateAvatarUpload(nextState: AvatarUploadState | null) {
    setAvatarUpload((current) => {
      if (current?.previewUrl && current.previewUrl !== nextState?.previewUrl) {
        URL.revokeObjectURL(current.previewUrl);
      }

      return nextState;
    });
  }

  async function handleAvatarFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';

    if (!selectedFile) {
      return;
    }

    form.clearErrors('root');
    setIsAvatarProcessing(true);

    try {
      const processedAvatar = await processAvatarUpload(selectedFile);
      updateAvatarUpload(processedAvatar);
      toast.success('Avatar prêt. Il sera enregistré avec le profil.');
    } catch (error) {
      const message = error instanceof Error
        ? error.message
        : 'Impossible de préparer cette image.';
      form.setError('root', { message });
      toast.error(message);
    } finally {
      setIsAvatarProcessing(false);
    }
  }

  async function uploadPendingAvatar() {
    if (!avatarUpload) {
      return null;
    }

    const prepareResult = await prepareAvatarUpload({
      contentType: avatarUpload.file.type as 'image/webp',
      filename: avatarUpload.file.name,
    });

    if (prepareResult.error || !prepareResult.data?.data) {
      throw prepareResult.error ?? new Error('Impossible de préparer l upload avatar.');
    }

    const preparedUpload = prepareResult.data.data;
    const uploadResponse = await fetch(preparedUpload.uploadUrl, {
      body: avatarUpload.file,
      headers: preparedUpload.headers,
      method: preparedUpload.method,
    });

    if (!uploadResponse.ok) {
      throw new Error('Impossible d envoyer le fichier avatar vers le stockage.');
    }

    const finalizeResult = await finalizeAvatarUpload({
      temporaryObjectKey: preparedUpload.temporaryObjectKey,
    });

    if (finalizeResult.error || !finalizeResult.data?.data) {
      throw finalizeResult.error ?? new Error('Impossible de finaliser l avatar.');
    }

    return finalizeResult.data.data.imageUrl;
  }

  async function handleSubmit(values: UpdateProfileFormOutput) {
    form.clearErrors('root');

    let nextImage = values.image ?? null;
    const currentImage = session.user.image ?? null;

    if (!avatarUpload && values.name === session.user.name && nextImage === currentImage) {
      toast.info('Aucune modification à enregistrer.');
      return;
    }

    try {
      if (avatarUpload) {
        setIsAvatarUploading(true);
        nextImage = await uploadPendingAvatar();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : getAuthErrorMessage(error);
      form.setError('root', { message });
      toast.error(message);
      setIsAvatarUploading(false);
      return;
    }

    const result = await updateCurrentUser({
      ...values,
      image: nextImage,
    });
    setIsAvatarUploading(false);

    if (result.error) {
      const message = getAuthErrorMessage(result.error);
      form.setError('root', { message });
      toast.error(message);
      return;
    }

    form.setValue('image', nextImage ?? '', {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
    updateAvatarUpload(null);
    await onAccountUpdated();
    toast.success('Profil mis à jour.');
  }

  async function handleResendVerificationEmail() {
    const result = await sendVerificationEmail(
      session.user.email,
      getBrowserUrl(`/auth/verify-email?email=${encodeURIComponent(session.user.email)}`),
    );

    if (result.error) {
      toast.error(getAuthErrorMessage(result.error));
      return;
    }

    toast.success('Un nouveau lien de confirmation a été envoyé.');
  }

  return (
    <form className={cn('rounded-[var(--radius-surface-4xl)] border border-border bg-card p-6 shadow-[0_8px_24px_rgba(15,23,42,0.03)]', className)} onSubmit={form.handleSubmit(handleSubmit)} {...props}>
      <FieldGroup className="gap-6">
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-foreground">Informations du compte</h2>
          <p className="text-sm leading-6 text-muted-foreground">
            Le nom et l avatar restent modifiables ici. L email et le rôle restent pilotés par Better Auth et l administration.
          </p>
        </div>

        <Field>
          <FieldLabel htmlFor="account-image-upload">Avatar</FieldLabel>
          <input type="hidden" {...form.register('image')} />
          <input
            ref={avatarInputRef}
            id="account-image-upload"
            data-testid="account-avatar-file-input"
            type="file"
            accept={getAvatarAcceptedFileTypes()}
            className="sr-only"
            onChange={(event) => void handleAvatarFileChange(event)}
          />
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={() => avatarInputRef.current?.click()}
              disabled={isBusy}
              className="group inline-flex w-fit shrink-0 items-center justify-center disabled:pointer-events-none disabled:opacity-50"
              aria-label="Modifier l’avatar"
            >
              <Avatar className="size-36 bg-card ring-2 ring-border ring-offset-4 ring-offset-background">
                <AvatarImage src={displayedAvatarUrl || undefined} alt={form.watch('name') || session.user.name} />
                <AvatarFallback className="bg-muted text-3xl font-semibold tracking-tight text-foreground">{avatarInitials}</AvatarFallback>
                <span className="absolute right-1.5 bottom-1.5 inline-flex size-9 items-center justify-center rounded-full bg-primary text-primary-foreground ring-4 ring-background transition-transform group-hover:scale-105">
                  {isAvatarProcessing || isAvatarUploading ? <Loader2Icon className="size-4 animate-spin" /> : <CameraIcon className="size-4" />}
                </span>
              </Avatar>
            </button>

            <div className="min-w-0 space-y-3">
              <Button
                type="button"
                variant="outline"
                data-testid="account-avatar-upload-trigger"
                disabled={isBusy}
                onClick={() => avatarInputRef.current?.click()}
                className="h-10 border-border bg-background px-3.5 text-sm font-medium text-foreground hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
              >
                <CameraIcon />
                Changer la photo
              </Button>
              <p className="text-sm text-muted-foreground">JPEG, PNG ou WebP, 15 Mo max.</p>
              {avatarUpload ? (
                <p className="text-sm text-foreground">Nouvel avatar prêt. Enregistre pour le publier.</p>
              ) : null}
            </div>
          </div>
          <FieldError errors={[form.formState.errors.image, form.formState.errors.root]} />
        </Field>

        <div className="grid gap-6 lg:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="account-name">Nom complet</FieldLabel>
            <Input
              id="account-name"
              data-testid="account-name-input"
              type="text"
              autoComplete="name"
              aria-invalid={form.formState.errors.name ? 'true' : 'false'}
              {...form.register('name')}
            />
            <FieldError errors={[form.formState.errors.name]} />
          </Field>

          <Field>
            <FieldLabel htmlFor="account-email">Email</FieldLabel>
            <Input id="account-email" value={session.user.email} type="email" disabled readOnly />
            <FieldDescription>
              {session.user.emailVerified ? 'Email déjà vérifié.' : 'Email non vérifié.'}
            </FieldDescription>
          </Field>
        </div>

        <div className="-mt-2 grid gap-6 lg:grid-cols-2">
          <Field>
            <FieldLabel htmlFor="account-role">Rôle</FieldLabel>
            <Input
              id="account-role"
              value={((session.user as { role?: string | null }).role ?? 'user') === 'admin' ? 'Administrateur' : 'Utilisateur'}
              disabled
              readOnly
            />
            <FieldDescription>Le rôle est défini par les règles d administration.</FieldDescription>
          </Field>
          <div className="hidden lg:block" />
        </div>

        {!session.user.emailVerified ? (
          <div className="rounded-[var(--radius-surface-3xl)] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-500/10 dark:text-amber-200">
            <p className="leading-6">
              Ton email n est pas encore vérifié. Certaines opérations de sécurité peuvent rester limitées tant que ce
              point n est pas résolu.
            </p>
            <Button
              type="button"
              variant="outline"
              className="mt-4 border-amber-300 bg-background text-amber-900 hover:bg-amber-100 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200 dark:hover:bg-amber-950/50"
              onClick={() => void handleResendVerificationEmail()}
            >
              <RefreshCcwIcon />
              Renvoyer le lien de confirmation
            </Button>
          </div>
        ) : null}

        <div className="flex justify-end">
          <Button
            data-testid="account-profile-submit"
            type="submit"
            disabled={isBusy}
            className="h-[49px] gap-2 border-black bg-black px-6 text-[15px] font-medium text-white shadow-none transition-colors hover:border-black/85 hover:bg-black/85 hover:text-white dark:border-border dark:bg-muted dark:text-foreground dark:hover:border-foreground/20 dark:hover:bg-muted/80 dark:hover:text-foreground"
          >
            {isBusy ? <Loader2Icon className="animate-spin" /> : <CheckIcon />}
            Enregistrer
          </Button>
        </div>
      </FieldGroup>
    </form>
  );
}
